import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  ExternalLink,
  Rocket,
  Loader2,
  RefreshCw,
  Mail,
  ChevronDown,
  Bitcoin,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import LaunchKitPaymentMethods from '@/components/launch-kit/LaunchKitPaymentMethods';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface Brief {
  id: string;
  project_name: string;
  industry: string;
  business_type: string;
  business_model: string | null;
  target_audience: string | null;
  description: string;
  primary_goal: string | null;
  investment_expected: string | null;
  budget_range: string | null;
  timeline: string;
  already_has: string | null;
  countries_targeted: string | null;
  languages: string | null;
  inspiration_links: string | null;
  competitors: string | null;
  faceless_preference: string | null;
  monetization_preference: string | null;
  preferred_platforms: string[] | null;
  additional_notes: string | null;
  has_content: boolean | null;
  has_audience: boolean | null;
  has_logo: boolean | null;
  has_domain: boolean | null;
  has_brand: boolean | null;
  full_name: string;
  email: string;
  review_status: string;
  decline_reason: string | null;
  admin_notes: string | null;
  invoice_url: string | null;
  invoice_storage_path: string | null;
  quoted_amount: number | null;
  reviewed_at: string | null;
  created_at: string;
  status: string;
}

const LaunchKitStatus = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('launch_kit_briefs')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (error) {
      toast.error('Could not load your project briefs.');
    } else {
      setBriefs((data || []) as unknown as Brief[]);
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/launch-kit/status');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const latest = briefs[0];

  return (
    <>
      <Helmet>
        <title>Project Status — AI Business Launch Kit | Faceless Aura Club</title>
        <meta name="description" content="Track the status of your AI Business Launch Kit project brief." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
        <LightBackground />

        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </header>

        <main className="relative z-10 max-w-3xl mx-auto px-6 py-10 md:py-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-5">
              <Rocket className="w-7 h-7 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              Your Launch Kit Project
            </h1>
            <p className="text-slate-600 max-w-xl mx-auto">
              We review every brief by hand. Once approved you'll find your invoice here ready to pay and kick off the build.
            </p>
          </motion.div>

          {!latest ? (
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-10 text-center space-y-5">
              <FileText className="w-10 h-10 text-slate-400 mx-auto" />
              <h2 className="text-xl font-semibold text-slate-900">No project brief yet</h2>
              <p className="text-slate-600">Submit a brief and our team will review it within a few business days.</p>
              <Button onClick={() => navigate('/launch-kit/brief')} className="bg-blue-600 hover:bg-blue-700 text-white">
                Submit a Project Brief
              </Button>
            </div>
          ) : (
            <StatusCard brief={latest} onResubmit={() => navigate('/launch-kit/brief')} />
          )}

          {briefs.length > 1 && (
            <section className="mt-10">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Previous submissions</h3>
              <div className="space-y-3">
                {briefs.slice(1).map((b) => (
                  <div key={b.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{b.project_name}</p>
                      <p className="text-xs text-slate-500">{new Date(b.created_at).toLocaleDateString()}</p>
                    </div>
                    <StatusBadge status={b.review_status} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    pending: { label: 'Under Review', cls: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3 h-3" /> },
    approved: { label: 'Approved', cls: 'bg-green-100 text-green-700', icon: <CheckCircle2 className="w-3 h-3" /> },
    paid: { label: 'In Production', cls: 'bg-blue-100 text-blue-700', icon: <Rocket className="w-3 h-3" /> },
    declined: { label: 'Needs Changes', cls: 'bg-rose-100 text-rose-700', icon: <XCircle className="w-3 h-3" /> },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${s.cls}`}>
      {s.icon}
      {s.label}
    </span>
  );
};

const StatusCard = ({ brief, onResubmit }: { brief: Brief; onResubmit: () => void }) => {
  const [generatingPay, setGeneratingPay] = useState(false);

  const handleGenerateBinancePay = async () => {
    setGeneratingPay(true);
    try {
      const { data, error } = await supabase.functions.invoke('binance-pay-launch-kit-brief', {
        body: { brief_id: brief.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const url = data?.checkoutUrl;
      if (!url) throw new Error('No checkout URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success('Binance Pay invoice opened in a new tab');
      // Refresh so the persisted invoice_url shows up
      setTimeout(() => window.location.reload(), 1500);
    } catch (err: any) {
      const msg = err?.message || 'Could not generate Binance Pay invoice';
      if (msg.includes('binance_not_configured') || msg.includes('not yet enabled')) {
        toast.error('Crypto payments are not enabled yet. Please try again soon.');
      } else {
        toast.error(msg);
      }
    } finally {
      setGeneratingPay(false);
    }
  };

  const { review_status } = brief;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
    >
      <div className="p-6 md:p-8 border-b border-slate-100">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{brief.project_name}</h2>
            <p className="text-sm text-slate-500 mt-1">
              Submitted {new Date(brief.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              {' · '}{brief.industry}
            </p>
          </div>
          <StatusBadge status={review_status} />
        </div>
      </div>

      <div className="p-6 md:p-8 space-y-6">
        {review_status === 'pending' && (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Under review by our team</h3>
            <p className="text-slate-600 max-w-md mx-auto">
              We're studying your brief to scope the build properly. You'll get an email when it's approved with a clear invoice
              and timeline — usually within <strong>2–4 business days</strong>.
            </p>
          </div>
        )}

        {review_status === 'approved' && (
          <div className="space-y-5">
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Approved — ready to start</h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Your project has been approved. Review the invoice below and complete payment to kick off the build.
              </p>
            </div>

            {brief.admin_notes && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Scope notes from our team</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{brief.admin_notes}</p>
              </div>
            )}

            {brief.quoted_amount && (
              <>
                <div className="rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 p-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Total Price</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      ${Number(brief.quoted_amount).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment acknowledgement</p>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    By proceeding with payment, I acknowledge and agree that I am purchasing an informational and
                    advisory PDF deliverable prepared specifically for my business launch request. This deliverable
                    may include business research, market investigation, launch strategy, operational guidance,
                    recommended tools, estimated costs, planning steps, and professional recommendations on how the
                    business could be structured, built, and launched.
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    I understand that this payment does not include the actual creation, registration, legal
                    incorporation, technical development, hiring, marketing execution, or guaranteed launch of the
                    business unless separately agreed in writing. I also understand that the information provided is
                    for educational, strategic, and planning purposes only and does not constitute legal, financial,
                    tax, investment, or regulated business advice.
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    I confirm that I have reviewed the quoted price and voluntarily authorize this payment for the
                    PDF deliverable and associated advisory guidance.
                  </p>
                </div>
              </>
            )}

            {brief.quoted_amount && (
              <LaunchKitPaymentMethods
                briefId={brief.id}
                amount={Number(brief.quoted_amount)}
                onGenerateBinance={handleGenerateBinancePay}
                generatingBinance={generatingPay}
              />
            )}

            {(brief.invoice_url || brief.invoice_storage_path) && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    let url = brief.invoice_url || '';
                    if (brief.invoice_storage_path) {
                      const { data, error } = await supabase.storage
                        .from('launch-kit-invoices')
                        .createSignedUrl(brief.invoice_storage_path, 300);
                      if (error) throw error;
                      url = data.signedUrl;
                    }
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (e: any) {
                    toast.error(e?.message || 'Could not open invoice');
                  }
                }}
                className="w-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                View detailed invoice PDF
                <ExternalLink className="w-3.5 h-3.5 ml-2" />
              </Button>
            )}
          </div>
        )}

        {review_status === 'paid' && (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
              <Rocket className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">In production</h3>
            <p className="text-slate-600 max-w-md mx-auto">
              Payment received. Our team is now building your project. You'll receive progress updates by email.
            </p>
          </div>
        )}

        {review_status === 'declined' && (
          <div className="space-y-5">
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-50 mb-4">
                <XCircle className="w-8 h-8 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Needs changes before we can build</h3>
              <p className="text-slate-600 max-w-md mx-auto">
                We can't take this on as currently scoped. See the notes below to understand how far we can go, then
                resubmit a refined brief.
              </p>
            </div>

            {brief.decline_reason && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-5">
                <p className="text-xs font-semibold text-rose-700 uppercase tracking-wider mb-2">Why &amp; what we can do</p>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{brief.decline_reason}</p>
              </div>
            )}

            {brief.admin_notes && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Additional notes</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{brief.admin_notes}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={onResubmit} className="bg-blue-600 hover:bg-blue-700 text-white flex-1">
                Submit a Revised Brief
              </Button>
              <Button asChild variant="outline" className="flex-1">
                <a href="mailto:hello@auradynamics.management">
                  <Mail className="w-4 h-4 mr-2" />
                  Discuss with our team
                </a>
              </Button>
            </div>
          </div>
        )}

        {/* Submitted brief details — viewable in any status */}
        <Collapsible className="border-t border-slate-100 pt-5 -mx-6 md:-mx-8 px-6 md:px-8">
          <CollapsibleTrigger className="flex items-center justify-between w-full text-left group">
            <div>
              <p className="font-semibold text-slate-900">View submitted brief</p>
              <p className="text-xs text-slate-500">See exactly what you sent us</p>
            </div>
            <ChevronDown className="w-5 h-5 text-slate-400 group-data-[state=open]:rotate-180 transition-transform" />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-5 space-y-4 text-sm">
            <BriefField label="Contact" value={`${brief.full_name} · ${brief.email}`} />
            <BriefField label="Industry" value={brief.industry} />
            <BriefField label="Business type" value={brief.business_type} />
            {brief.business_model && <BriefField label="Business model" value={brief.business_model} />}
            {brief.target_audience && <BriefField label="Target audience" value={brief.target_audience} />}
            <BriefField label="Project description" value={brief.description} multiline />
            {brief.primary_goal && <BriefField label="Primary goal" value={brief.primary_goal} />}
            {brief.investment_expected && <BriefField label="Investment expected" value={brief.investment_expected} />}
            {brief.budget_range && <BriefField label="Budget range" value={brief.budget_range} />}
            <BriefField label="Timeline" value={brief.timeline} />
            {brief.already_has && <BriefField label="What you already have" value={brief.already_has} multiline />}
            {brief.countries_targeted && <BriefField label="Countries targeted" value={brief.countries_targeted} />}
            {brief.languages && <BriefField label="Languages" value={brief.languages} />}
            {brief.faceless_preference && <BriefField label="Faceless preference" value={brief.faceless_preference} />}
            {brief.monetization_preference && <BriefField label="Monetization" value={brief.monetization_preference} />}
            {brief.preferred_platforms && brief.preferred_platforms.length > 0 && (
              <BriefField label="Preferred platforms" value={brief.preferred_platforms.join(', ')} />
            )}
            {brief.competitors && <BriefField label="Competitors" value={brief.competitors} multiline />}
            {brief.inspiration_links && <BriefField label="Inspiration links" value={brief.inspiration_links} multiline />}
            {brief.additional_notes && <BriefField label="Additional notes" value={brief.additional_notes} multiline />}
            <div className="flex flex-wrap gap-2 pt-2">
              {brief.has_logo && <Chip>Has logo</Chip>}
              {brief.has_brand && <Chip>Has brand</Chip>}
              {brief.has_domain && <Chip>Has domain</Chip>}
              {brief.has_content && <Chip>Has content</Chip>}
              {brief.has_audience && <Chip>Has audience</Chip>}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </motion.div>
  );
};

const BriefField = ({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-slate-800 ${multiline ? 'whitespace-pre-wrap' : ''}`}>{value}</p>
  </div>
);

const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
    ✓ {children}
  </span>
);

export default LaunchKitStatus;
